<template>
  <Layout>
    <template v-slot:header>
      <!-- Header / Stepper -->
      <q-stepper
        ref="stepper"
        v-model="step"
        class="stepper-no-content"
        :header-nav="false"
        color="primary"
        flat
        done-color="positive"
        :contracted="$q.screen.lt.md"
        style="min-width: 240px"
      >
        <q-step
          :name="1"
          :title="$page.props.stepper.platform.label"
          icon="las la-project-diagram"
          active-icon="las la-project-diagram"
          done-icon="las la-check"
          :done="step > 1"
          :header-nav="step > 1"
        />

        <q-step
          :name="2"
          :title="$page.props.stepper.type.label"
          icon=""
          active-icon=""
          :done-icon="$page.props.stepper.type.done_icon"
          :done="step > 2"
          :header-nav="step > 2"
        />

        <q-step
          :name="3"
          :title="$page.props.stepper.details.label"
          active-icon="las la-pen"
          icon="las la-lock"
          :done="step > 3"
          :error="form_progress_state == 'error'"
          error-color="negative"
          error-icon="las la-exclamation-triangle"
          :header-nav="step > 3"
        />
      </q-stepper>
    </template>

    <template v-slot:default>
      <q-page class="centered-container column full-height">
        <!-- Platform Selection -->
        <template v-if="step === 1">
          <div class="col-3">
            <div class="fit row wrap justify-center items-start content-start text-center q-mt-xl">
              <!-- Search input -->
              <div
                class="col-12 q-mb-lg"
                style="max-width: 400px"
              >
                <q-input
                  v-model="filter_search"
                  autofocus
                  for="search-exchange-account-input"
                  color="drawer-background"
                  :placeholder="$page.props.i18n.search_exchange"
                  outlined
                  rounded
                  standout
                  dense
                >
                  <template v-slot:prepend>
                    <q-icon name="search" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      v-if="filter_search !== ''"
                      name="close"
                      class="cursor-pointer"
                      @click="filter_search = ''"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Filter type -->
              <div class="col-12 justify-center items-center align-center content-center">
                <q-btn-toggle
                  v-model="filter_type"
                  color="drawer-background"
                  class="text-caption"
                  toggle-color="primary"
                  text-color="dark-text"
                  unelevated
                  no-caps
                  rounded
                  :options="[
                    { label: $page.props.i18n.filter_popular, value: 'popular' },
                    { label: `${$page.props.i18n.filter_all} (\u00A0${$page.props.exchanges.count_all}\u00A0)`, value: 'all' },
                  ]"
                />
              </div>
            </div>
          </div>

          <!-- Exchange List -->
          <div class="col-9 align-around">
            <div class="row q-col-gutter-md q-pa-lg q-px-xl q-mt-md justify-center items-stretch">
              <div
                v-for="exchange in $page.props.exchanges.data"
                :key="exchange.id"
                class="col-xs-12 col-sm-6 col-md-4 col-lg-2 exchange-card-col p-4"
              >
                <Link
                  class="fit"
                  :href="exchange.link"
                  as="button"
                  :disabled="exchange.maintenance"
                  style="width: 100%; height: 100%;"
                >
                  <q-card class="cursor-pointer q-hoverable border-radius-6 exchange-card card-item fit">
                    <span
                      v-if="!exchange.maintenance"
                      class="q-focus-helper"
                    />
                    <q-card-section class="q-px-md q-py-lg">
                      <div class="exchange-card-chip-wrapper">
                        <q-chip
                          v-if="exchange.maintenance"
                          :label="$page.props.i18n.under_maintenance"
                          class="experimental-chip"
                          color="negative"
                          size="sm"
                          outline
                        />
                        <q-chip
                          v-if="exchange.csv_ai_enabled"
                          :label="$page.props.i18n.experimental"
                          class="experimental-chip"
                          color="primary"
                          size="sm"
                          outline
                        />
                      </div>


                      <div
                        class="column items-center q-pa-md overlay_wrappper"
                        :class="{ 'disabled-qcard': exchange.maintenance }"
                      >
                        <q-avatar
                          round
                          size="42px"
                        >
                          <q-img
                            height="46px"
                            width="46px"
                            fit="contain"
                            :alt="exchange.label"
                            :src="exchange.logo_url"
                            style="border-radius: 50%"
                            @error="exchange.logo_url = '/illustrations/exchange_logo_default.svg'"
                          />
                        </q-avatar>
                        <div class="text-body2 q-pt-md text-bold text-capitalize text-center">
                          {{ exchange.label }}
                        </div>
                      </div>
                    </q-card-section>
                  </q-card>
                </Link>
              </div>


              <div
                v-if="$page.props.exchanges.meta.count == 0"
                class="col-12 text-center"
              >
                <div class="text-caption text-grey-8">
                  {{ $page.props.i18n.no_result }}
                </div>

                <div class="row q-py-lg">
                  <div class="col-12 text-center">
                    <span class="bg-pastel-purple q-pa-sm q-px-md rounded-borders">
                      {{ $page.props.i18n.no_result_for_platform }}
                      <Link
                        class="anchor"
                        :href="$page.props.exchanges.new_csv_path"
                      >{{
                        $page.props.i18n.try_csv_importer
                      }}</Link>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!-- /Exchange List -->

            <div
              v-if="$page.props.exchanges.meta.last > 1"
              class="row justify-center q-mt-md q-pb-md"
            >
              <Pagination
                :meta="$page.props.exchanges.meta"
                centered
              />
            </div>
          </div>
        </template>

        <!-- Import Type Selection -->
        <template v-else-if="step === 2">
          <div class="row q-col-gutter-xl q-pa-xl q-mt-xl justify-center">
            <div class="col-12 row flex-center">
              <img
                class="h-12 w-12 rounded-full overflow-hidden"
                :alt="$page.props.exchange.label"
                :src="$page.props.exchange.logo_url"
              >
              &nbsp;
              <span class="text-h4 q-ml-sm">{{ $page.props.exchange.label }}</span>
            </div>

            <div
              v-for="method in $page.props.exchange.add_methods"
              :key="method.id"
              class="col-xs-12 col-sm-6 col-md-4 self-stretch"
            >
              <q-card class="cursor-pointer q-hoverable border-radius-6 exchange-card card-item fit column">
                <q-card-section class="items-center q-pa-xl text-center col column">
                  <q-avatar
                    v-if="method.logo_url"
                    size="56px"
                    round
                    :color="method.icon_color"
                  >
                    <q-img
                      :src="method.logo_url"
                      :alt="method.label"
                      width="32px"
                      height="32px"
                      fit="contain"
                    />
                  </q-avatar>
                  <q-avatar
                    v-else
                    size="56px"
                    :color="method.icon_color"
                  >
                    <q-icon
                      color="black"
                      size="md"
                      :name="method.icon"
                    />
                  </q-avatar>
                  <div class="text-subtitle1 text-bold text-capitalize q-mt-md">
                    {{ method.label }}
                  </div>
                  <div class="text-caption">
                    {{ method.description }}
                  </div>
                  <div class="row no-wrap justify-center q-mt-lg pt-8 full-width" style="margin-top: auto;">
                    <Link
                      class="full-width"
                      :href="method.link"
                    >
                      <q-btn
                        v-if="method.active"
                        class="full-width"
                        color="primary"
                        unelevated
                        
                        :label="$page.props.i18n.continue"
                        no-caps
                      />
                    </Link>
                    <q-btn
                      v-if="!method.active"
                      color="primary"
                      unelevated
                       
                      disable
                      no-caps
                      :label="$page.props.i18n.available_soon"
                    />
                  </div>
                </q-card-section>

                <template v-if="method.id == 'comptacrypto-csv-ai'">
                  <div
                    class="absolute-top-left"
                    style="left: 8px; top: 8px;"
                  >
                    <q-chip
                      :label="$page.props.i18n.experimental"
                      class="experimental-chip"
                      color="primary"
                      size="sm"
                      outline
                    />
                  </div>
                  <div
                    class="absolute-top-right"
                    style="right: 8px; top: 8px;"
                  >
                    <q-chip
                      class="csv_ai_chip"
                      icon="auto_awesome"
                      size="sm"
                    >
                      AI
                    </q-chip>
                  </div>
                </template>
              </q-card>
            </div>

            <div
              v-if="$page.props.exchange.import_type_message"
              class="row center text-center q-mx-xl"
            >
              <q-banner
                class="bg-pastel-blue q-pa-md"
                dense
              >
                <template v-slot:avatar>
                  <q-icon
                    name="las la-info-circle"
                    class=""
                    size="lg"
                  />
                </template>
                {{ $page.props.exchange.import_type_message }}
              </q-banner>
            </div>
          </div>
        </template>

        <!-- Details -->
        <template v-else-if="step === 3">
          <div class="row q-col-gutter-xl q-pa-xl q-mt-xl justify-center">
            <!-- <div class="col-12 text-center">
              <h4 class="text-bold q-mb-lg">Renseignez les informations du compte</h4>
            </div> -->
          </div>

          <div class="col-12 row flex-center q-my-xl">
            <img
              class="h-12 w-12 rounded-full overflow-hidden"
              :alt="$page.props.exchange.label"
              :src="$page.props.exchange.logo_url"
            >
            &nbsp;
            <span class="text-h4 q-ml-sm">{{ $page.props.exchange.label }}</span>
          </div>

          <div class="fit row wrap justify-center items-start content-start q-pa-md">
            <div
              class="col-12"
              style="max-width: 460px"
            >
              <form
                v-if="!form_progress_show"
                @submit.prevent="submit"
              >
                <!-- Account name -->
                <span class="text-input-label">{{ $page.props.i18n.account_name }}</span>
                <q-input
                  v-model="form.exchange_account.name"
                  class="full-width q-mt-sm q-mb-md"
                  outlined
                  dense
                  :placeholder="$page.props.i18n.account_name_place_holder"
                  lazy-rules
                  :rules="[
                    (val) => !!val || $page.props.i18n.required,
                    (val) => val.length <= 50 || $page.props.i18n.account_name_limit_max_char_50,
                  ]"
                />

                <template v-if="$page.props.import_method.type == 'file'">
                  <div
                    v-if="uploader_drop_zone_show"
                    class="q-my-md q-mb-md"
                  >
                    <Uploader
                      :multiple="true"
                      @signed_id="(blob) => input_file_signed_id(blob)"
                    />
                  </div>

                  <!-- Show csv file to import with remove button -->
                  <div
                    v-if="uploader_files_show"
                    class="q-my-md q-mb-md"
                  >
                    <q-list>
                      <q-item-label
                        class="q-px-none q-pb-xs"
                        header
                      >
                        {{ $page.props.i18n.file_to_import }}
                      </q-item-label>
                      <q-item class="q-mb-md q-px-none">
                        <q-item-section avatar>
                          <q-icon name="las la-file" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ uploader_uploaded_filename }}</q-item-label>
                        </q-item-section>
                        <q-item-section side>
                          <q-btn
                            round
                            dense
                            flat
                            icon="las la-trash"
                            color="negative"
                            @click="input_file_remove"
                          />
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </div>

                  <!-- Link to documentation -->
                  <q-banner
                    class="bg-pastel-green text-dark q-pa-md q-mb-lg"
                    dense
                  >
                    <template v-slot:avatar>
                      <q-icon
                        name="las la-info-circle"
                        class=""
                        size="md"
                      />
                    </template>
                    <div class="text-caption text-dark">
                      {{ $page.props.import_method.doc_text || $page.props.i18n.no_documentation }}
                      <br>
                      <!-- documentation -->
                      <a
                        :href="$page.props.import_method.doc_url || 'https://comptacrypto.com/documentation/'"
                        target="_blank"
                        class="text-bold href-link"
                      >
                        {{ $page.props.i18n.see_documentation }}
                      </a>
                    </div>
                  </q-banner>
                </template>

                <!-- api_key -->
                <template v-if="$page.props.import_method.fields.hasOwnProperty('api_key')">
                  <span class="text-input-label">{{ $page.props.i18n.api_key }}</span>
                  <q-input
                    v-model="form.exchange_account.api_key"
                    :hint="$page.props.import_method.fields.api_key.hint"
                    class="full-width q-mt-sm q-mb-md"
                    outlined
                    dense
                    :placeholder="$page.props.i18n.api_key_place_holder"
                    lazy-rules
                    :rules="[
                      (val) => !!val || $page.props.i18n.required,
                    ]"
                  />
                </template>

                <!-- api_secret -->
                <template v-if="$page.props.import_method.fields.hasOwnProperty('secret_key')">
                  <span class="text-input-label">{{ $page.props.i18n.secret_key }}</span>
                  <q-input
                    v-model="form.exchange_account.secret_key"
                    :hint="$page.props.import_method.fields.secret_key.hint"
                    class="full-width q-mt-sm q-mb-md"
                    outlined
                    dense
                    :type="form_exchange_account_secret_key_show ? 'text' : 'password'"
                    :placeholder="$page.props.i18n.secret_key_place_holder"
                    lazy-rules
                    :rules="[
                      (val) => !!val || $page.props.i18n.required,
                    ]"
                  >
                    <template v-slot:append>
                      <q-icon
                        :name="form_exchange_account_secret_key_show ? 'visibility' : 'visibility_off'"
                        class="cursor-pointer"
                        @click="form_exchange_account_secret_key_show = !form_exchange_account_secret_key_show"
                      />
                    </template>
                  </q-input>
                </template>

                <!-- passphrase -->
                <template v-if="$page.props.import_method.fields.hasOwnProperty('passphrase')">
                  <span class="text-input-label">{{ $page.props.i18n.passphrase }}</span>
                  <q-input
                    v-model="form.exchange_account.passphrase"
                    class="full-width q-mt-sm q-mb-md"
                    outlined
                    dense
                    :type="form_exchange_account_passphrase_show ? 'text' : 'password'"
                    :placeholder="$page.props.i18n.passphrase_place_holder"
                    lazy-rules
                    :rules="[
                      (val) => !!val || $page.props.i18n.required,
                    ]"
                  >
                    <template v-slot:append>
                      <q-icon
                        :name="form_exchange_account_passphrase_show ? 'visibility' : 'visibility_off'"
                        class="cursor-pointer"
                        @click="form_exchange_account_passphrase_show = !form_exchange_account_passphrase_show"
                      />
                    </template>
                  </q-input>
                </template>

                <!-- credential_organization_id -->
                <template v-if="$page.props.import_method.fields.hasOwnProperty('credential_organization_id')">
                  <span class="text-input-label">{{ $page.props.i18n.credential_organization_id }}</span>
                  <q-input
                    v-model="form.exchange_account.credential_organization_id"
                    class="full-width q-mt-sm q-mb-md"
                    outlined
                    dense
                    :type="text"
                    :placeholder="$page.props.i18n.organization_id_place_holder"
                    lazy-rules
                    :rules="[
                      (val) => !!val || $page.props.i18n.required,
                    ]"
                  />
                </template>

                <!-- sub_account_name -->
                <template v-if="$page.props.import_method.fields.hasOwnProperty('sub_account_name')">
                  <span class="text-input-label">{{ $page.props.i18n.sub_account_name }}</span>
                  <q-input
                    v-model="form.exchange_account.sub_account_name"
                    class="full-width q-mt-sm q-mb-md"
                    outlined
                    dense
                    type="text"
                    :placeholder="$page.props.i18n.sub_account_name_place_holder"
                    lazy-rules
                  />
                </template>

                <!-- public_address -->
                <template v-if="$page.props.import_method.fields.hasOwnProperty('public_address')">
                  <span class="text-input-label">{{ $page.props.i18n.public_address }}</span>
                  <q-input
                    v-model="form.exchange_account.public_address"
                    class="full-width q-mt-sm q-mb-md"
                    outlined
                    dense
                    :placeholder="$page.props.i18n.public_address_place_holder"
                    lazy-rules
                    :rules="[(val) => (val && val.length > 0) || $page.props.i18n.required]"
                  />
                </template>

                <q-btn
                  id="submit-btn"
                  :label="$page.props.i18n.submit"
                  type="submit"
                  unelevated
                  :disable="form.processing"
                  size="lg"
                  color="primary"
                  class="full-width"
                  style="font-size: 16px"
                />
              </form>

              <!-- Loading / Done (or Error) -->
              <div
                v-if="form_progress_show"
                class="container-fluid row justify-center q-mt-xl"
              >
                <animatedCircularProgress
                  :done="!form.processing"
                  :error="form_progress_state == 'error'"
                  :loading-msg="$page.props.i18n.progress_message_loading"
                >
                  <template v-slot:success>
                    <p
                      class="text-bold q-my-lg"
                      style="font-size: 16px"
                    >
                      {{ $page.props.i18n.progress_message_success }}
                    </p>
                    <p class="text-center text-caption">
                      {{ $page.props.i18n.progress_message_success_redirect }}
                    </p>
                  </template>

                  <template v-slot:error>
                    <template v-if="!form.errors?.max_exchange_accounts?.length">
                      <p
                        class="text-bold q-my-lg"
                        style="font-size: 16px"
                      >
                        {{ $page.props.i18n.progress_message_error }}
                      </p>
                      <p class="text-center text-caption text-negative">
                        {{ $page.props.flash.alert }}
                      </p>
                      <div class="col-12">
                        <q-btn
                          color="primary"
                          class="q-mt-lg"
                          icon="las la-undo-alt"
                          unelevated
                          no-caps
                          @click="form_progress_show = false"
                        />
                      </div>
                    </template>
                    <template v-else>
                      <p
                        class="text-bold q-my-lg"
                        style="font-size: 16px"
                      >
                        {{ $page.props.i18n.progress_message_error }}
                      </p>
                      <p class="text-body2 text-negative">
                        {{ form.errors?.max_exchange_accounts[0] || $page.props.i18n.progress_message_error_upgrade_text }}
                      </p>
                      <div class="col-12">
                        <Link :href="$page.props.upgrade_subscription_link">
                          <q-btn
                            color="positive"
                            class="q-mt-lg"
                            icon="las la-shopping-cart"
                            unelevated
                            :label="$page.props.i18n.progress_message_error_upgrade_button"
                            no-caps
                          />
                        </Link>
                      </div>
                    </template>
                  </template>
                </animatedCircularProgress>
              </div>
            </div>
          </div>
        </template>
      </q-page>
    </template>

    <template v-slot:footer>
      <div class="row full-width no-wrap justify-center q-my-md items-center">
        <q-avatar
          rounded
          color="transparent"
          size="18px"
        >
          <Jdenticon
            :text="$page.props.shared.workspace.id"
            :size="18"
          />
        </q-avatar>
        &nbsp;
        <span class=" text-subtitle2 text-bold items-center">{{
          $page.props.shared.workspace.name
        }}</span>
      </div>
    </template>
  </Layout>
</template>
<script>
import Layout from '@/layouts/wide.vue';
import { Link } from '@inertiajs/vue3';
import Jdenticon from '@/shared/jdenticon.vue';
import Pagination from '@/shared/pagination.vue';
import Uploader from '@/shared/uploader.vue';
import AnimatedCircularProgress from "@/shared/animated_circular_progress.vue";

export default {
  components: {
    AnimatedCircularProgress,
    Jdenticon,
    Layout,
    Link,
    Pagination,
    Uploader,
  },
  props: {
    user: {
      type: Object,
      required: false,
    },
    form_action: {
      type: String,
      required: true,
    },
  },
  remember: [
    'form',
    'uploader_drop_zone_show',
    'uploader_files_show',
    'uploader_uploaded_filename',
  ],
  data() {
    return {
      step: this.$page.props.step,
      filter_search: this.$page.props.exchanges.filter_search,
      filter_type: this.$page.props.exchanges.filter_type,
      filter_type_syncing_from_props: false,
      uploader_drop_zone_show: true,
      uploader_files_show: false,
      uploader_uploaded_filename: null,
      form_exchange_account_secret_key_show: false,
      form_exchange_account_passphrase_show: false,
      form: this.$inertia.form({
        exchange_account: {
          name: null,
          public_address: null,
          api_key: null,
          secret_key: null,
          passphrase: null,
          credential_organization_id: null,
          sub_account_name: null,
          input_file_signed_id: null,
        },
      }),
      form_progress_show: false,
      form_progress_state: "loading",
    };
  },
  computed: {
  },
  watch: {
    filter_search(new_value) {
      this.$inertia.get(
        this.$page.props.exchanges.search_path,
        { search: new_value },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['exchanges'],
        },
      );
    },
    filter_type(new_value) {
      if (this.filter_type_syncing_from_props) {
        this.filter_type_syncing_from_props = false;
        return;
      }
      this.$inertia.get(
        this.$page.props.exchanges.search_path,
        { type: new_value },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['exchanges'],
        },
      );
    },
    '$page.props.exchanges.filter_type'(new_value) {
      if (!new_value || new_value === this.filter_type) return;

      this.filter_type_syncing_from_props = true;
      this.filter_type = new_value;
    }
  },
  methods: {
    input_file_signed_id: function (blob) {
      this.uploader_uploaded_filename = blob.filename;
      this.form.exchange_account.input_file_signed_id = blob.signed_id;
      this.uploader_drop_zone_show = false;
      this.uploader_files_show = true;
    },
    input_file_remove: function () {
      this.uploader_uploaded_filename = null;
      this.form.exchange_account.input_file_signed_id = null;
      this.uploader_drop_zone_show = true;
      this.uploader_files_show = false;
    },
    submit() {
      this.form_progress_show = true;
      this.form.post(this.$page.props.form_action, {
        preserveScroll: true,
        onError: (e) => {
          this.form_progress_state = 'error';
          console.log(e);
        },
        onSuccess: () => {
          this.form_progress_state = 'success';
          this.form.reset();
          setTimeout(() => {
            this.$inertia.get(this.$page.props.success_redirect)
          }, 500);
        },
      });
    },
  },
};
</script>

<style scoped>
@media (min-width: 1024px) {
  .exchange-card-col {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>
